import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '@/auth/AuthContext';
import { PasswordRequirements } from '@/auth/PasswordRequirements';
import { authErrorMessage } from '@/auth/util_authErrors';
import { checkPassword, MIN_PASSWORD_LENGTH } from '@/auth/util_password';
import { OnboardingStepHeader } from './OnboardingShell';
import {
  onboardingLabelClass,
  onboardingPrimaryButtonClass,
  onboardingRowClass,
  onboardingSecondaryButtonClass,
  onboardingSpinnerClass,
} from './onboarding_styles';

interface OnboardingStepAccountProps {
  /** The address the invitation is locked to. Not editable — it is the identity. */
  invitedEmail: string;
  onBack: () => void;
  /** True while the parent runs the Google popup. */
  busy: boolean;
  onGoogle: () => void;
  /** Errors from the parent's own work — the popup, and redemption itself. */
  error: string | null;
  onError: (message: string | null) => void;
}

/**
 * Step 3 — the credentials. Password and confirmation as in the mock, with
 * Google alongside for the partners on Workspace.
 *
 * Its own screen rather than the staff invitation panel reused, because the two
 * are not the same question. Staff arrive at a page that has to cope with an
 * account they may already have; a manager arrives at the end of a wizard, at an
 * address Eighty Days has just invited, and offering them "I already have a
 * password" invites them to answer a question about a state they cannot be in.
 *
 * They can be in it by accident, though: the account is created here and the
 * invitation is redeemed after, so an attempt interrupted in between leaves a
 * real account behind at the invited address. That is a recovery, not a choice —
 * so it is never on offer, and appears only when Firebase reports it.
 */
export function OnboardingStepAccount({
  invitedEmail,
  onBack,
  busy,
  onGoogle,
  error,
  onError,
}: OnboardingStepAccountProps) {
  const { createAccountWithEmail, signInWithEmail, sendPasswordReset } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Separate from `submitting`: sending a reset email must not look like the
  // registration button is working.
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Set only by Firebase telling us the address is taken. See the note above.
  const [existingAccount, setExistingAccount] = useState(false);

  const checks = checkPassword(password, confirmPassword);
  const disabled = busy || submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      if (existingAccount) {
        await signInWithEmail(invitedEmail, password);
      } else {
        await createAccountWithEmail(invitedEmail, password, displayName);
      }
      // Nothing else to do here: the invitation page is watching for the session
      // and redeems the moment it appears.
    } catch (err) {
      console.error(err);
      if ((err as { code?: string }).code === 'auth/email-already-in-use') {
        setExistingAccount(true);
        setConfirmPassword('');
        onError(null);
        setNotice(
          'You already started setting up this account. Enter the password you chose to finish, or reset it below.',
        );
      } else {
        onError(authErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReset() {
    onError(null);
    setResetting(true);
    try {
      await sendPasswordReset(invitedEmail);
      setNotice(`Password reset email sent to ${invitedEmail}.`);
    } catch (err) {
      console.error(err);
      onError(authErrorMessage(err));
    } finally {
      setResetting(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className='ed-step-content tw:space-y-6'>
      <OnboardingStepHeader
        title={existingAccount ? 'Sign In to Continue' : 'Create Your Account'}
        blurb={existingAccount ? 'Your login already exists' : 'Set up your login credentials'}
      />

      <div>
        <label htmlFor='loginEmail' className={onboardingLabelClass}>
          Login Email
        </label>
        {/* Locked, not merely prefilled: redemption requires the signed-in
            address to match the invitation exactly, so an editable field here
            would only offer a way to fail. */}
        <input
          id='loginEmail'
          type='email'
          value={invitedEmail}
          readOnly
          autoComplete='email'
          className='ed-input-luxe tw:rounded-sm tw:text-white/70'
        />
        <p className='tw:mt-2 tw:text-xs tw:text-white/40'>
          This will be your username. Your invitation is locked to this address.
        </p>
      </div>

      {!existingAccount && (
        <div>
          <label htmlFor='displayName' className={onboardingLabelClass}>
            Your Name *
          </label>
          <input
            id='displayName'
            type='text'
            required
            value={displayName}
            disabled={disabled}
            placeholder='Full name'
            autoComplete='name'
            onChange={(e) => setDisplayName(e.target.value)}
            className='ed-input-luxe tw:rounded-sm'
          />
        </div>
      )}

      <div className={onboardingRowClass}>
        <div>
          <label htmlFor='password' className={onboardingLabelClass}>
            Password *
          </label>
          <input
            id='password'
            type='password'
            required
            minLength={existingAccount ? undefined : MIN_PASSWORD_LENGTH}
            value={password}
            disabled={disabled}
            placeholder={existingAccount ? 'Your password' : `Min ${MIN_PASSWORD_LENGTH} characters`}
            autoComplete={existingAccount ? 'current-password' : 'new-password'}
            onChange={(e) => setPassword(e.target.value)}
            className='ed-input-luxe tw:rounded-sm'
          />
        </div>
        {!existingAccount && (
          <div>
            <label htmlFor='confirmPassword' className={onboardingLabelClass}>
              Confirm Password *
            </label>
            <input
              id='confirmPassword'
              type='password'
              required
              value={confirmPassword}
              disabled={disabled}
              placeholder='Re-enter password'
              autoComplete='new-password'
              onChange={(e) => setConfirmPassword(e.target.value)}
              className='ed-input-luxe tw:rounded-sm'
            />
          </div>
        )}
      </div>

      {!existingAccount && <PasswordRequirements checks={checks} className='tw:rounded-sm tw:p-4' />}

      {notice && <p className='tw:text-sm tw:text-ed-gold'>{notice}</p>}
      {error && <p className='tw:text-sm tw:text-ed-red'>{error}</p>}

      <div className='tw:flex tw:gap-4 tw:pt-2'>
        <button type='button' onClick={onBack} disabled={disabled} className={onboardingSecondaryButtonClass}>
          Back
        </button>
        <button
          type='submit'
          disabled={disabled || (existingAccount ? !password : !checks.ok)}
          aria-busy={submitting || undefined}
          className={onboardingPrimaryButtonClass}
        >
          {submitting && <Loader2 className={onboardingSpinnerClass} />}
          {submitting ? 'Please wait…' : existingAccount ? 'Sign In & Continue' : 'Complete Registration'}
        </button>
      </div>

      {existingAccount && (
        <button
          type='button'
          onClick={() => void handleReset()}
          disabled={resetting}
          aria-busy={resetting || undefined}
          className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-white/50 tw:underline tw:underline-offset-4 tw:transition-colors tw:hover:text-ed-gold'
        >
          {resetting && <Loader2 className='tw:size-3 tw:animate-spin' />}
          Forgot password? Send a reset email
        </button>
      )}

      <div className='tw:flex tw:items-center tw:gap-4 tw:pt-2'>
        <span className='tw:h-px tw:flex-1 tw:bg-white/10' />
        <span className='tw:text-xs tw:tracking-[0.2em] tw:text-white/30 tw:uppercase'>or</span>
        <span className='tw:h-px tw:flex-1 tw:bg-white/10' />
      </div>

      {/* For partners on Google Workspace. Redemption still checks the address, so
          picking the wrong Google account fails the same way as before. */}
      <button
        type='button'
        onClick={onGoogle}
        disabled={disabled}
        aria-busy={busy || undefined}
        className='tw:inline-flex tw:w-full tw:items-center tw:justify-center tw:gap-3 tw:rounded-sm tw:bg-white tw:px-6 tw:py-4 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-opacity tw:hover:opacity-90 tw:disabled:opacity-60'
      >
        {busy ? <Loader2 className='tw:size-5 tw:animate-spin' /> : <FcGoogle className='tw:size-5' />}
        {busy ? 'Signing in…' : `Continue with Google as ${invitedEmail}`}
      </button>
    </form>
  );
}
